import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { BannerContainer, BannerContentWrapper, BannerImage, BannerImageCaption, BannerImageWrapper, BannerWrapper } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/__internal__/styled";
import { InsiderSavingsSummaryRenderer } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/InsiderSavingsSummaryRenderer";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { useQuery as useIsEligibleForAnnualBillingQuery } from "@faire/web-api--source/api/retailer/insider/is-eligible-for-annual-billing/get-hooks";
import { trackInsiderLoggedOutMarketingPageSavingsSummaryView } from "@faire/web-api--source/events/insider/view/loggedOutMarketingPageSavingsSummary";
import React from "react";
import styled from "styled-components";
var getMessage = function(hasFreeDutyAdvertising) {
    if (hasFreeDutyAdvertising) {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "ccb4js",
            defaultMessage: [
                {
                    type: 0,
                    value: "As an Insider member, you\u2019ll get free shipping and import duties on thousands of unique brands\u2014meaning less risk to try new products, and more savings to put back into your store."
                }
            ]
        });
    } else {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "hu0OnN",
            defaultMessage: [
                {
                    type: 0,
                    value: "As an Insider member, you'll get free shipping on thousands of unique brands\u2014meaning less risk to try new products, and more savings to put back into your store."
                }
            ]
        });
    }
};
export var InsiderSavingsBanner = function() {
    var isSSR = useIsSSR();
    var isTabletAndAbove = useViewport().isTabletAndAbove;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useIsEligibleForAnnualBillingQuery = useIsEligibleForAnnualBillingQuery({
        enabled: isLoggedInRetailer
    }), data = _useIsEligibleForAnnualBillingQuery.data, isLoading = _useIsEligibleForAnnualBillingQuery.isLoading;
    var _useInsiderFeatureEligibility = useInsiderFeatureEligibility(), hasFreeDutyAdvertising = _useInsiderFeatureEligibility.hasFreeDutyAdvertising, hasSavingsBreakdown = _useInsiderFeatureEligibility.hasSavingsBreakdown;
    var imageSrc = isTabletAndAbove ? optimizeImageUrlFillRectangle({
        url: "https://cdn.faire.com/fastly/df7b0dd2d6818ae73c5677999c68a317548fa214ac7ae280a9c05416bd3f3938.jpeg"
    }, isSSR, 440, 500) : optimizeImageUrlFillRectangle({
        url: "https://cdn.faire.com/fastly/cde4d69237cbd52befd037fcb6c30876fbec0c754c1f0d3adae6d3eff58ed5e7.jpeg"
    }, isSSR, 375, 314);
    var ref = useDoActionWhenVisible(function() {
        trackInsiderLoggedOutMarketingPageSavingsSummaryView();
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {});
    }
    return /*#__PURE__*/ _jsx(BannerWrapper, {
        ref: ref,
        children: /*#__PURE__*/ _jsxs(BannerContainer, {
            children: [
                /*#__PURE__*/ _jsxs(BannerContentWrapper, {
                    children: [
                        /*#__PURE__*/ _jsx(StyledTypography, {
                            variant: {
                                mobileAndAbove: "displaySSerifRegular",
                                desktopAndAbove: "displayMSerifRegular"
                            },
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "5z6TuZ",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Try new products, without the added costs"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "2x"
                        }),
                        /*#__PURE__*/ _jsx(StyledTypography, {
                            variant: {
                                mobileAndAbove: "paragraphSansRegular",
                                desktopAndAbove: "subheadingSansRegular"
                            },
                            children: getMessage(hasFreeDutyAdvertising)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(BannerImageWrapper, {
                    align: {
                        mobileAndAbove: "center",
                        tabletAndAbove: "flex-start"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                            tabletAndAbove: /*#__PURE__*/ _jsx(BannerImage, {
                                src: imageSrc
                            })
                        }),
                        hasSavingsBreakdown ? /*#__PURE__*/ _jsx(SavingsSummaryWrapper, {
                            $mobileBackgroundImage: imageSrc,
                            align: "center",
                            "data-test-id": "insider-savings-summary-wrapper",
                            children: /*#__PURE__*/ _jsx(InsiderSavingsSummaryRenderer, {
                                isEligibleForAnnualBilling: data === null || data === void 0 ? void 0 : data.is_eligible_for_annual_billing
                            })
                        }) : null,
                        /*#__PURE__*/ _jsx(BannerImageCaption, {
                            children: MARKETING_BRAND_NAME
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "2x"
                        }),
                        hasSavingsBreakdown ? /*#__PURE__*/ _jsx(Typography, {
                            variant: "labelSansRegular",
                            color: Core.text.subdued,
                            children: (data === null || data === void 0 ? void 0 : data.is_eligible_for_annual_billing) ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "t8zoBr",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "*Monthly cost of the Insider annual plan."
                                            }
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "5C7pRi",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "**Savings reflected on orders from Insider brands only."
                                            }
                                        ]
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "BVh9Si",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "*Only applies to orders of brands with Insider benefits"
                                    }
                                ]
                            })
                        }) : null
                    ]
                })
            ]
        })
    });
};
var MARKETING_BRAND_NAME = "kiko+ & gg*";
var StyledTypography = styled(Typography).withConfig({
    componentId: "sc-a6cce5eb-0"
})([
    "max-width:300px;",
    "{max-width:395px;}"
], responsiveMediaQueries.desktopAndAbove);
var SavingsSummaryWrapper = styled(Column).withConfig({
    componentId: "sc-a6cce5eb-1"
})([
    "background:url(",
    ");width:100%;padding:",
    ";",
    "{background:unset;width:unset;padding:unset;position:absolute;bottom:40px;left:-47px;}",
    "{bottom:unset;top:130px;}"
], function(param) {
    var $mobileBackgroundImage = param.$mobileBackgroundImage;
    return $mobileBackgroundImage;
}, getSpacing("4x", 0), responsiveMediaQueries.tabletAndAbove, responsiveMediaQueries.desktopAndAbove);
